import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import FeatureCard from 'tio-reimagined/components/feature-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import CtaBanner from 'tio-reimagined/components/cta-banner';
import PageHero from 'tio-reimagined/components/page-hero';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhGraduationCap,
  PhHandshake,
  PhArrowRight,
  PhStar,
  PhMagnifyingGlass,
  PhCertificate,
  PhChalkboardTeacher,
  PhBookOpen,
  PhBuildingOffice,
  PhSparkle,
} from 'ember-phosphor-icons';

interface Partner {
  id: string;
  name: string;
  category: string;
  description: string;
  discount: string;
  rating: number;
  programs: number;
  featured: boolean;
}

const DEMO_PARTNERS: Partner[] = [
  {
    id: 'arizona-state',
    name: 'Arizona State University',
    category: 'University',
    description:
      "Top-ranked online programs tailored for working professionals. Earn your degree on your schedule with ASU's flexible format.",
    discount: 'Up to 46% tuition reduction',
    rating: 4.8,
    programs: 120,
    featured: true,
  },
  {
    id: 'purdue-global',
    name: 'Purdue University Global',
    category: 'University',
    description:
      'Career-focused online degrees with credit for work experience. Personalized learning paths for adult learners.',
    discount: 'Up to 38% tuition reduction',
    rating: 4.6,
    programs: 85,
    featured: true,
  },
  {
    id: 'coursera-business',
    name: 'Coursera for Business',
    category: 'Online Platform',
    description:
      'Access certificates from Google, IBM, Meta, and top universities. Build skills in data, tech, and leadership.',
    discount: 'Employer-sponsored access',
    rating: 4.7,
    programs: 500,
    featured: false,
  },
  {
    id: 'guild-education',
    name: 'Guild Education',
    category: 'Education Network',
    description:
      'Curated learning marketplace connecting you to high-quality programs aligned with career advancement.',
    discount: 'Tuition-free programs available',
    rating: 4.5,
    programs: 200,
    featured: false,
  },
  {
    id: 'strayer-university',
    name: 'Strayer University',
    category: 'University',
    description:
      'Flexible online and hybrid programs designed for working adults. Accelerated formats to finish faster.',
    discount: 'Up to 25% tuition reduction',
    rating: 4.3,
    programs: 45,
    featured: false,
  },
  {
    id: 'udemy-business',
    name: 'Udemy Business',
    category: 'Online Platform',
    description:
      'On-demand courses in tech, business, and personal development. Learn at your own pace with expert instructors.',
    discount: 'Full catalog access included',
    rating: 4.4,
    programs: 3000,
    featured: false,
  },
];

export default class LpnIndexPage extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  get partners() {
    return DEMO_PARTNERS;
  }

  get featuredPartners() {
    return this.partners.filter((p) => p.featured);
  }

  get allPartners() {
    return this.partners;
  }

  get categories() {
    return [...new Set(this.partners.map((p) => p.category))];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero
        @gradientClass="bg-gradient-to-br from-teal-600 via-cyan-700 to-blue-800"
        @decoration="circles"
        @rounded="2xl"
      >
        <div class="flex items-center gap-3 mb-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
            <PhHandshake class="h-7 w-7 text-white" />
          </div>
          <div>
            <Heading @level={{2}} class="text-white text-2xl font-bold">
              Learning Partner Network
            </Heading>
            <Text class="text-teal-100 text-sm">
              Exclusive education partners with discounted programs
            </Text>
          </div>
        </div>

        <div class="flex items-center gap-8 mt-6">
          <div class="grid grid-cols-3 gap-6">
            <div class="text-center">
              <div class="text-3xl font-bold text-white">{{this.allPartners.length}}</div>
              <Text class="text-teal-200 text-sm">Partners</Text>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white">3,950+</div>
              <Text class="text-teal-200 text-sm">Programs</Text>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white">46%</div>
              <Text class="text-teal-200 text-sm">Max Savings</Text>
            </div>
          </div>
        </div>
      </PageHero>

      {{! Search & Filter }}
      <Card @padding="sm">
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-2 flex-1 min-w-[200px] bg-gray-50 rounded-lg px-3 py-2">
            <PhMagnifyingGlass class="h-4 w-4 text-gray-400" />
            <Text class="text-gray-400 text-sm">Search partners, programs, or topics...</Text>
          </div>
          {{#each this.categories as |cat|}}
            <Chip @intent="primary">{{cat}}</Chip>
          {{/each}}
        </div>
      </Card>

      {{! Featured Partners }}
      <SectionHeader
        @icon={{PhSparkle}}
        @title="Featured Partners"
        @subtitle="Curated programs with the best value for your goals"
        @iconClass="text-teal-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        {{#each this.featuredPartners as |partner|}}
          <div
            class="bg-white rounded-xl border-2 border-teal-200 dark:border-teal-800 p-6 relative"
          >
            <div
              class="absolute -top-3 right-4 flex items-center gap-1 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium"
            >
              <PhSparkle class="h-3 w-3" />
              Featured
            </div>
            <div class="flex items-start gap-4">
              <div
                class="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-100 dark:bg-teal-900/30 shrink-0"
              >
                <PhBuildingOffice class="h-7 w-7 text-teal-600" />
              </div>
              <div class="flex-1">
                <Heading @level={{3}} class="font-bold text-lg">{{partner.name}}</Heading>
                <div class="flex items-center gap-2 mt-1 mb-2">
                  <Chip @intent="primary">{{partner.category}}</Chip>
                  <div class="flex items-center gap-1">
                    <PhStar class="h-3.5 w-3.5 text-amber-500" />
                    <Text class="text-sm font-medium">{{partner.rating}}</Text>
                  </div>
                  <Text class="text-gray-400 text-sm">•</Text>
                  <Text class="text-gray-500 text-sm">{{partner.programs}} programs</Text>
                </div>
                <Text class="text-gray-600 text-sm mb-3">
                  {{partner.description}}
                </Text>
                <div
                  class="flex items-center justify-between bg-teal-50 dark:bg-teal-900/20 rounded-lg px-3 py-2"
                >
                  <Text class="text-teal-700 dark:text-teal-300 text-sm font-medium">
                    {{partner.discount}}
                  </Text>
                  <Button @intent="primary" @appearance="outlined">
                    Explore
                    <PhArrowRight class="ml-1 h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! All Partners }}
      <SectionHeader
        @icon={{PhGraduationCap}}
        @title="All Partners"
        @subtitle="Browse the complete network"
        @iconClass="text-blue-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {{#each this.allPartners as |partner|}}
          <Card class="hover:border-teal-300 dark:hover:border-teal-700 transition-colors">
            <div class="flex items-center gap-3 mb-3">
              <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <PhGraduationCap class="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <Heading @level={{4}} class="font-semibold text-sm">{{partner.name}}</Heading>
                <Chip @intent="primary">{{partner.category}}</Chip>
              </div>
            </div>
            <Text class="text-gray-600 text-xs line-clamp-2 mb-3">
              {{partner.description}}
            </Text>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1">
                <PhStar class="h-3 w-3 text-amber-500" />
                <Text class="text-xs font-medium">{{partner.rating}}</Text>
                <Text class="text-gray-400 text-xs ml-1">{{partner.programs}} programs</Text>
              </div>
              <Text class="text-teal-600 dark:text-teal-400 text-xs font-medium">
                {{partner.discount}}
              </Text>
            </div>
          </Card>
        {{/each}}
      </div>

      {{! Benefits of LPN }}
      <SectionHeader
        @icon={{PhHandshake}}
        @title="Why Use the Network?"
        @subtitle="Exclusive benefits for employees"
        @iconClass="text-emerald-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FeatureCard
          @icon={{PhCertificate}}
          @title="Verified Quality"
          @description="Every partner is vetted for program quality, outcomes, and student satisfaction."
          @color="emerald"
        />
        <FeatureCard
          @icon={{PhChalkboardTeacher}}
          @title="Flexible Learning"
          @description="Online, hybrid, and in-person options designed for working professionals."
          @color="blue"
        />
        <FeatureCard
          @icon={{PhBookOpen}}
          @title="Stackable Credentials"
          @description="Earn certificates that stack toward degrees. Start small, build big."
          @color="violet"
        />
      </div>

      {{! CTA }}
      <CtaBanner
        @heading="Not Sure Where to Start?"
        @description="Our career advisor can match you with the right partner and program based on your career goals and existing experience."
        @theme="teal"
      >
        <:action>
          <LinkTo @route="authenticated.reimagined.advisor">
            <Button @intent="primary">
              Get Matched
              <PhArrowRight class="ml-2 h-4 w-4" />
            </Button>
          </LinkTo>
        </:action>
      </CtaBanner>
    </VStack>
  </template>
}
